import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import comuniData from "../data/comuni_cap.json";
import { 
  Calculator, FileText, Users, Brain, PlusCircle, 
  ChevronRight, ChevronDown, Sparkles, Download, Eye, X, Check, Link2, Copy,
  Building2, Minus, Plus, Trash2, AlertCircle, TrendingUp,
  Landmark, FileDown, FileSignature, FileCheck, Handshake,
  Mail, MessageCircle, Scale, Award, LogOut
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import PresentationProfessional from "@/components/PresentationProfessional";

const BACKEND_URL = "";
const API = `${BACKEND_URL}/api`;

// Helper function per download robusto dei documenti
const downloadDocument = async (url, filename) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    toast.success(`${filename} scaricato con successo`);
  } catch (error) {
    console.error('Download error:', error);
    toast.error('Errore durante il download');
  }
};

const CATEGORY_CONFIG = {
  contabilita: { 
    icon: Calculator, 
    label: "Contabilità", 
    color: "#818CF8",
    bgColor: "bg-indigo-400",
    hoverColor: "hover:bg-indigo-300",
    lightBg: "bg-indigo-50"
  },
  fiscale: { 
    icon: FileText, 
    label: "Fiscale", 
    color: "#F9A8D4",
    bgColor: "bg-pink-400",
    hoverColor: "hover:bg-pink-300",
    lightBg: "bg-pink-50"
  },
  personale: { 
    icon: Users, 
    label: "770 e CU", 
    color: "#6EE7B7",
    bgColor: "bg-emerald-400",
    hoverColor: "hover:bg-emerald-300",
    lightBg: "bg-emerald-50"
  },
  consulenza: { 
    icon: Brain, 
    label: "Consulenza", 
    color: "#C4B5FD",
    bgColor: "bg-violet-400",
    hoverColor: "hover:bg-violet-300",
    lightBg: "bg-violet-50"
  },
  extra: { 
    icon: PlusCircle, 
    label: "Extra", 
    color: "#7DD3FC",
    bgColor: "bg-sky-400",
    hoverColor: "hover:bg-sky-300",
    lightBg: "bg-sky-50"
  },
  riscossione: { 
    icon: Landmark, 
    label: "Riscossione", 
    color: "#FDE68A",
    bgColor: "bg-amber-400",
    hoverColor: "hover:bg-amber-300",
    lightBg: "bg-amber-50"
  },
  contenzioso: { 
    icon: Scale, 
    label: "Contenzioso", 
    color: "#FCA5A5",
    bgColor: "bg-red-400",
    hoverColor: "hover:bg-red-300",
    lightBg: "bg-red-50"
  },
  bandi_contributi: { 
    icon: Award, 
    label: "Bandi", 
    color: "#6EE7B7",
    bgColor: "bg-emerald-500",
    hoverColor: "hover:bg-emerald-400",
    lightBg: "bg-emerald-50"
  }
};

// Servizi NON inclusi con prezzi di riferimento
const SERVIZI_NON_INCLUSI = [
  { nome: "Assistenza verifiche fiscali", prezzo: "da €275" },
  { nome: "Ricorsi tributari", prezzo: "a preventivo" },
  { nome: "Perizie di stima", prezzo: "a preventivo" },
  { nome: "Consulenza straordinaria", prezzo: "€115/ora" },
  { nome: "Pratiche camerali straordinarie", prezzo: "da €70" },
  { nome: "Registrazione contratti locazione", prezzo: "€185" },
  { nome: "Successioni", prezzo: "a preventivo" },
  { nome: "Operazioni straordinarie", prezzo: "a preventivo" }
];

// Adeguamenti per superamento volumi
const ADEGUAMENTI_VOLUMI = [
  { 
    servizio: "Contabilità Ordinaria", 
    descrizione: "Oltre 600 registrazioni",
    adeguamento: "€2,75/reg (601-2000) o €2,40/reg (oltre 2000)"
  },
  { 
    servizio: "Contabilità Semplificata", 
    descrizione: "Oltre 360 documenti",
    adeguamento: "€1,65/registrazione"
  },
  { 
    servizio: "Dichiarazione IVA", 
    descrizione: "Cambio fascia fatturato",
    adeguamento: "Adeguamento allo scaglione superiore"
  },
  { 
    servizio: "Dichiarazione Redditi", 
    descrizione: "Cambio fascia ricavi",
    adeguamento: "Adeguamento allo scaglione superiore"
  },
  { 
    servizio: "Bilancio", 
    descrizione: "Cambio fascia totale attivo",
    adeguamento: "Adeguamento allo scaglione superiore"
  }
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(value);
};

export default function PreventivoBuilder() {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const [tariffe, setTariffe] = useState({});
  const [activeCategory, setActiveCategory] = useState("contabilita");
  const [activeSubcategory, setActiveSubcategory] = useState(null);
  const [selectedServices, setSelectedServices] = useState({});
  const [serviceQuantities, setServiceQuantities] = useState({});
  const [servicePrices, setServicePrices] = useState({});
  const [serviceDescriptions, setServiceDescriptions] = useState({});
  const [cliente, setCliente] = useState({
    ragione_sociale: "",
    via: "",
    cap: "",
    citta: "",
    provincia: "",
    tipo_identificativo: "partita_iva",
    codice_fiscale: "",
    partita_iva: "",
    pec: "",
    email: "",
    telefono: "",
    legale_rappresentante: "",
    cliente_esistente: false
  });
  const [scontoPercentuale, setScontoPercentuale] = useState(0);
  const [giornoIncasso, setGiornoIncasso] = useState(10);
  const [registrazioniOrd, setRegistrazioniOrd] = useState(600);
  const [showClienteDialog, setShowClienteDialog] = useState(false);
  const [showPresentazione, setShowPresentazione] = useState(false);
  const [savedPreventivo, setSavedPreventivo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [clienteConfermato, setClienteConfermato] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [cfDialog, setCfDialog] = useState({ open: false, callback: null });
  const [cfInput, setCfInput] = useState("");
  const [cittaSuggestions, setCittaSuggestions] = useState([]);
  const [showCittaSuggestions, setShowCittaSuggestions] = useState(false);
  const [viaSuggestions, setViaSuggestions] = useState([]);
  const [showViaSuggestions, setShowViaSuggestions] = useState(false);
  const cittaRef = useRef(null);
  const viaRef = useRef(null);
  const viaDebounceRef = useRef(null);

  // Normalizza query per Nominatim (rimuove apostrofi e caratteri speciali)
  const normalizeQuery = useCallback((text) => text.replace(/[''`]/g, ''), []);

  // Lookup CAP tramite Nominatim (OpenStreetMap) - gratuito
  const lookupCap = useCallback(async (via, citta) => {
    if (!via || !citta) return;
    try {
      const q = encodeURIComponent(`${normalizeQuery(via)}, ${citta}, Italia`);
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${q}&format=json&addressdetails=1&countrycodes=it&limit=1`,
        { headers: { 'User-Agent': 'PreventivoApp/1.0' } }
      );
      const data = await res.json();
      if (data?.[0]?.address?.postcode) {
        const cap = data[0].address.postcode;
        setCliente(prev => ({ ...prev, cap }));
        toast.success(`CAP trovato: ${cap}`);
      } else {
        toast.info("CAP non trovato automaticamente. Inseriscilo manualmente.");
      }
    } catch {
      // Silenzioso
    }
  }, [normalizeQuery]);

  // Autocomplete indirizzo tramite Nominatim
  const handleViaChange = useCallback((value) => {
    setCliente(prev => ({ ...prev, via: value }));
    if (viaDebounceRef.current) clearTimeout(viaDebounceRef.current);
    if (value.length >= 4) {
      viaDebounceRef.current = setTimeout(async () => {
        try {
          const q = encodeURIComponent(`${normalizeQuery(value)}, Italia`);
          const res = await fetch(
            `https://nominatim.openstreetmap.org/search?q=${q}&format=json&addressdetails=1&countrycodes=it&limit=6`,
            { headers: { 'User-Agent': 'PreventivoApp/1.0' } }
          );
          const data = await res.json();
          const results = data
            .filter(d => d.address?.road)
            .map(d => ({
              via: [d.address.road, d.address.house_number].filter(Boolean).join(' '),
              citta: d.address.city || d.address.town || d.address.village || '',
              provincia: d.address.state || '',
              cap: d.address.postcode || '',
              display: d.display_name
            }))
            // Deduplica per via+citta
            .filter((v, i, arr) => arr.findIndex(a => a.via === v.via && a.citta === v.citta) === i);
          setViaSuggestions(results);
          setShowViaSuggestions(results.length > 0);
        } catch {
          setViaSuggestions([]);
          setShowViaSuggestions(false);
        }
      }, 400);
    } else {
      setViaSuggestions([]);
      setShowViaSuggestions(false);
    }
  }, [normalizeQuery]);

  const selectVia = useCallback((suggestion) => {
    // Trova la provincia (sigla) dal database comuni
    let sigla = '';
    if (suggestion.citta) {
      const comune = comuniData.find(c => c.n.toLowerCase() === suggestion.citta.toLowerCase());
      if (comune) sigla = comune.s;
    }
    setCliente(prev => ({
      ...prev,
      via: suggestion.via,
      citta: suggestion.citta,
      cap: suggestion.cap,
      provincia: sigla || prev.provincia
    }));
    setShowViaSuggestions(false);
    if (suggestion.cap) {
      toast.success(`Indirizzo completo: ${suggestion.via}, ${suggestion.cap} ${suggestion.citta}`);
    }
  }, []);

  // Ricerca comune per autocompletamento CAP e provincia
  const handleCittaChange = useCallback((value) => {
    setCliente(prev => ({ ...prev, citta: value }));
    if (value.length >= 2) {
      const search = value.toLowerCase();
      const matches = comuniData
        .filter(c => c.n.toLowerCase().startsWith(search))
        .slice(0, 8);
      setCittaSuggestions(matches);
      setShowCittaSuggestions(matches.length > 0);
    } else {
      setCittaSuggestions([]);
      setShowCittaSuggestions(false);
    }
  }, []);

  const selectComune = useCallback((comune) => {
    const hasMultipleCap = comune.c.length > 1;
    setCliente(prev => {
      const updated = {
        ...prev,
        citta: comune.n,
        provincia: comune.s,
        cap: hasMultipleCap ? prev.cap : comune.c[0]
      };
      if (hasMultipleCap && updated.via) {
        lookupCap(updated.via, comune.n);
      }
      return updated;
    });
    setShowCittaSuggestions(false);
  }, [lookupCap]);

  useEffect(() => {
    fetchTariffe();
  }, []);

  const fetchTariffe = async () => {
    try {
      const response = await axios.get(`${API}/tariffe`);
      setTariffe(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Errore nel caricamento tariffe:", error);
      toast.error("Errore nel caricamento delle tariffe");
      setIsLoading(false);
    }
  };

  // Carica preventivo esistente per la modifica
  useEffect(() => {
    if (!editId || Object.keys(tariffe).length === 0) return;
    
    const loadPreventivo = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const res = await axios.get(`${API}/preventivi/${editId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const p = res.data;
        
        // Imposta dati cliente
        const clienteData = p.cliente || {};
        // Se c'è il firmatario nel preventivo, recupera il CF
        if (p.firmatario?.cf && !clienteData.cf_firmatario) {
          clienteData.cf_firmatario = p.firmatario.cf;
        }
        setCliente(clienteData);
        setClienteConfermato(true);
        setScontoPercentuale(p.sconto_percentuale || 0);
        setGiornoIncasso(p.giorno_incasso || 10);
        setIsEditMode(true);
        
        // Ricostruisci i servizi selezionati dalle tariffe
        const newSelected = {};
        const newPrices = {};
        const newQuantities = {};
        const newDescriptions = {};
        
        for (const servizio of (p.servizi || [])) {
          // Cerca il servizio corrispondente nelle tariffe
          let found = null;
          let foundKey = null;
          
          for (const [catKey, catVal] of Object.entries(tariffe)) {
            if (!catVal || typeof catVal !== 'object') continue;
            // Cerca nei servizi diretti
            for (const s of (catVal.servizi || [])) {
              if (s.nome === servizio.nome || (servizio.categoria === catKey && s.descrizione === servizio.descrizione)) {
                found = { ...s, categoria: catKey, billing_type: servizio.billing_type };
                foundKey = `${catKey}_${s.id}`;
                break;
              }
            }
            if (found) break;
            // Cerca nelle sottocategorie
            for (const [, subVal] of Object.entries(catVal.sottocategorie || {})) {
              for (const s of (subVal.servizi || [])) {
                if (s.nome === servizio.nome || (servizio.categoria === catKey && s.descrizione === servizio.descrizione)) {
                  found = { ...s, categoria: catKey, billing_type: servizio.billing_type };
                  foundKey = `${catKey}_${s.id}`;
                  break;
                }
              }
              if (found) break;
            }
            if (found) break;
          }
          
          if (found && foundKey) {
            newSelected[foundKey] = found;
            if (servizio.quantita > 1) newQuantities[foundKey] = servizio.quantita;
            if (servizio.prezzo_totale) newPrices[foundKey] = servizio.prezzo_totale;
            if (servizio.descrizione) newDescriptions[foundKey] = servizio.descrizione;
          }
        }
        
        setSelectedServices(newSelected);
        setServicePrices(newPrices);
        setServiceQuantities(newQuantities);
        setServiceDescriptions(newDescriptions);
        
        toast.success(`Preventivo "${p.cliente?.ragione_sociale}" caricato per la modifica`);
      } catch (error) {
        console.error("Errore nel caricamento preventivo:", error);
        toast.error("Impossibile caricare il preventivo");
      }
    };
    
    loadPreventivo();
  }, [editId, tariffe]);

  const addService = (categoryKey, serviceId) => {
    const key = `${categoryKey}_${serviceId}`;
    if (selectedServices[key]) return;
    
    const category = tariffe[categoryKey];
    let service = null;
    
    // Cerca prima nei servizi diretti della categoria
    if (category.servizi) {
      service = category.servizi.find(s => s.id === serviceId);
    }
    
    // Se non trovato, cerca nelle sottocategorie
    if (!service && category.sottocategorie) {
      for (const [subKey, subCat] of Object.entries(category.sottocategorie)) {
        if (subCat.servizi) {
          service = subCat.servizi.find(s => s.id === serviceId);
          if (service) break;
        }
      }
    }
    
    if (!service) {
      console.error(`Servizio ${serviceId} non trovato in ${categoryKey}`);
      return;
    }
    
    // Determina il billing_type se non presente
    const billingType = service.billing_type || (
      service.tipo === "fisso_mensile" || 
      service.tipo === "fisso_trimestrale" || 
      service.tipo === "fisso_annuale" ? "monthly" : "one_off"
    );
    
    setSelectedServices(prev => ({
      ...prev,
      [key]: { ...service, categoria: categoryKey, billing_type: billingType }
    }));
    
    if (!serviceQuantities[key]) {
      setServiceQuantities(prev => ({ ...prev, [key]: 1 }));
    }
    if (!servicePrices[key] && service.prezzo_default) {
      setServicePrices(prev => ({ ...prev, [key]: service.prezzo_default }));
    }
    
    toast.success(`${service.nome} aggiunto`);
  };

  const removeService = (key) => {
    setSelectedServices(prev => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });
  };

  // Sostituisce uno scaglione con un altro nella stessa sottocategoria
  const switchTier = (categoryKey, oldServiceId, newServiceId) => {
    const oldKey = `${categoryKey}_${oldServiceId}`;
    const newKey = `${categoryKey}_${newServiceId}`;
    if (oldKey === newKey) return;
    
    // Rimuovi il vecchio
    removeService(oldKey);
    // Aggiungi il nuovo
    setTimeout(() => addService(categoryKey, newServiceId), 50);
  };

  // Raggruppa i servizi per scaglioni all'interno di una sottocategoria
  const groupServicesByTier = (servizi) => {
    // Servizi "fisso_mensile" e "range" della stessa sottocategoria sono scaglioni
    const tiers = servizi.filter(s => 
      s.tipo === "fisso_mensile" || s.tipo === "range" || s.tipo === "fisso_annuale"
    );
    const others = servizi.filter(s => 
      s.tipo !== "fisso_mensile" && s.tipo !== "range" && s.tipo !== "fisso_annuale"
    );
    
    // Se ci sono 2+ tiers dello stesso tipo base, raggruppali
    if (tiers.length >= 2) {
      return { tiered: tiers, standalone: others };
    }
    return { tiered: [], standalone: servizi };
  };

  const updateQuantity = (key, delta) => {
    setServiceQuantities(prev => ({
      ...prev,
      [key]: Math.max(1, (prev[key] || 1) + delta)
    }));
  };

  const setQuantity = (key, value) => {
    const numValue = parseInt(value) || 1;
    setServiceQuantities(prev => ({
      ...prev,
      [key]: Math.max(1, numValue)
    }));
  };

  const updatePrice = (key, value) => {
    setServicePrices(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
  };

  const updateDescription = (key, value) => {
    setServiceDescriptions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const calculateServicePrice = (service, key) => {
    const quantity = serviceQuantities[key] || 1;
    const customPrice = servicePrices[key];
    
    // Se l'utente ha impostato un prezzo custom, usalo direttamente
    if (customPrice !== undefined) {
      return { unitPrice: customPrice, annualPrice: customPrice };
    }
    
    let unitPrice = 0;
    let annualPrice = 0;
    
    // Caso speciale: Contabilità Ordinaria con registrazioni previste
    if (service.id === 'cont_ordinaria_600' && registrazioniOrd > 600) {
      const baseAnnual = service.prezzo_annuale || (service.prezzo_mensile * 12);
      const extraRegs = registrazioniOrd - 600;
      let extraCost = 0;
      if (registrazioniOrd <= 2000) {
        extraCost = extraRegs * 2.75;
      } else {
        extraCost = (2000 - 600) * 2.75 + (registrazioniOrd - 2000) * 2.40;
      }
      annualPrice = baseAnnual + extraCost;
      unitPrice = annualPrice / 12;
      return { unitPrice, annualPrice };
    }
    
    switch (service.tipo) {
      case "fisso_mensile":
        unitPrice = service.prezzo_mensile;
        annualPrice = unitPrice * 12;
        break;
      case "fisso_trimestrale":
        unitPrice = service.prezzo_trimestrale;
        annualPrice = unitPrice * 4;
        break;
      case "fisso_annuale":
        unitPrice = service.prezzo_annuale;
        annualPrice = unitPrice;
        break;
      case "variabile":
        unitPrice = service.prezzo_unitario || service.prezzo_orario || 0;
        annualPrice = unitPrice * quantity;
        break;
      case "una_tantum":
        unitPrice = service.prezzo_unitario;
        annualPrice = unitPrice * quantity;
        break;
      case "variabile_descrizione":
        unitPrice = service.prezzo_unitario;
        annualPrice = unitPrice;
        break;
      case "range":
        unitPrice = service.prezzo_default;
        annualPrice = unitPrice;
        break;
      case "base_piu_variabile":
        const basePrice = service.prezzo_base || 0;
        const perUnit = service.prezzo_per_dipendente || 0;
        annualPrice = basePrice + (perUnit * quantity);
        break;
      default:
        // Fallback: prova tutti i campi prezzo disponibili
        annualPrice = service.prezzo_annuale || service.prezzo_unitario || (service.prezzo_mensile ? service.prezzo_mensile * 12 : 0);
    }
    
    return { unitPrice, annualPrice };
  };

  // Helper: ottieni sottocategorie per la categoria attiva
  const getSubcategories = () => {
    const categoria = tariffe[activeCategory];
    if (!categoria?.sottocategorie) return [];
    
    // Aggiungi "Altro" se ci sono servizi generici
    const entries = Object.entries(categoria.sottocategorie);
    if (categoria.servizi?.length > 0) {
      entries.push(["altro", { nome: "Altro", servizi: categoria.servizi }]);
    }
    return entries;
  };

  // Helper: renderizza servizi per la categoria attiva
  const renderTierGroup = (servizi, categoryKey, subName) => {
    const groupId = `${categoryKey}_${servizi[0]?.id?.split('_').slice(0, -1).join('_') || subName}`;
    const isExpanded = expandedGroups[groupId];
    
    // Check if any tier in this group is selected
    const selectedTier = servizi.find(s => !!selectedServices[`${categoryKey}_${s.id}`]);
    const config = CATEGORY_CONFIG[categoryKey];
    
    // Get price summary for header
    const firstPrice = servizi[0];
    const lastPrice = servizi[servizi.length - 1];
    let priceLabel = "";
    if (firstPrice.prezzo_mensile && lastPrice.prezzo_mensile) {
      priceLabel = `${formatCurrency(firstPrice.prezzo_mensile)} - ${formatCurrency(lastPrice.prezzo_mensile)}/mese`;
    } else if (firstPrice.prezzo_annuale && lastPrice.prezzo_annuale) {
      priceLabel = `${formatCurrency(firstPrice.prezzo_annuale)} - ${formatCurrency(lastPrice.prezzo_annuale)}/anno`;
    } else if (firstPrice.prezzo_min) {
      priceLabel = `da ${formatCurrency(firstPrice.prezzo_min)}`;
    }

    return (
      <div key={groupId} className="col-span-2" data-testid={`tier-group-${groupId}`}>
        {/* Group header */}
        <div
          onClick={() => setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }))}
          className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${
            selectedTier
              ? 'border-emerald-300 bg-emerald-50'
              : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
          }`}
          data-testid={`tier-group-header-${groupId}`}
        >
          <div className="flex items-center gap-2 flex-1">
            {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            <div>
              <h4 className="font-semibold text-slate-900 text-sm">{subName}</h4>
              {selectedTier ? (
                <p className="text-xs text-emerald-600 font-medium mt-0.5">
                  Selezionato: {selectedTier.nome}
                </p>
              ) : (
                <p className="text-xs text-slate-500 mt-0.5">{servizi.length} scaglioni disponibili</p>
              )}
            </div>
          </div>
          <div className="text-right">
            <span className="font-mono text-xs" style={{ color: config.color }}>{priceLabel}</span>
          </div>
        </div>

        {/* Expanded tier options */}
        {isExpanded && (
          <div className="mt-1 ml-4 space-y-1" data-testid={`tier-options-${groupId}`}>
            {servizi.map(service => {
              const key = `${categoryKey}_${service.id}`;
              const isSelected = !!selectedServices[key];
              return (
                <div
                  key={service.id}
                  data-testid={`tier-option-${service.id}`}
                  onClick={() => {
                    if (isSelected) return;
                    // Se c'è un altro scaglione selezionato, sostituiscilo
                    if (selectedTier && selectedTier.id !== service.id) {
                      switchTier(categoryKey, selectedTier.id, service.id);
                    } else {
                      addService(categoryKey, service.id);
                    }
                  }}
                  className={`flex items-center justify-between p-2.5 rounded-md border transition-all cursor-pointer ${
                    isSelected
                      ? 'border-emerald-300 bg-emerald-50'
                      : 'border-slate-150 bg-slate-50/50 hover:bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2 flex-1">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'
                    }`}>
                      {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{service.nome}</p>
                      {service.descrizione && (
                        <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{service.descrizione}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    {service.tipo === "fisso_mensile" && (
                      <span className="font-mono text-sm font-bold" style={{ color: config.color }}>
                        {formatCurrency(service.prezzo_mensile)}<span className="text-xs font-normal">/mese</span>
                      </span>
                    )}
                    {service.tipo === "fisso_annuale" && (
                      <span className="font-mono text-sm font-bold" style={{ color: config.color }}>
                        {formatCurrency(service.prezzo_annuale)}<span className="text-xs font-normal">/anno</span>
                      </span>
                    )}
                    {service.tipo === "range" && (
                      <span className="font-mono text-xs" style={{ color: config.color }}>
                        {formatCurrency(service.prezzo_min)} - {formatCurrency(service.prezzo_max)}
                      </span>
                    )}
                    {service.tipo === "fisso" && (
                      <span className="font-mono text-sm font-bold" style={{ color: config.color }}>
                        {formatCurrency(service.prezzo_unitario)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderServicesForCategory = () => {
    const categoria = tariffe[activeCategory];
    if (!categoria) return null;

    // Se ha sottocategorie
    if (categoria.sottocategorie) {
      if (activeSubcategory === null) {
        // Mostra tutti organizzati per sottocategoria con raggruppamento scaglioni
        return (
          <>
            {Object.entries(categoria.sottocategorie).map(([subKey, sub]) => {
              const { tiered, standalone } = groupServicesByTier(sub.servizi || []);
              return (
                <div key={subKey} className="mb-2">
                  <p className="text-xs font-bold text-slate-500 uppercase mb-2 px-1 border-b border-slate-200 pb-1">{sub.nome}</p>
                  <div className="space-y-2">
                    {tiered.length > 0 && renderTierGroup(tiered, activeCategory, sub.nome)}
                    {standalone.map(service => renderServiceCard(service, activeCategory))}
                  </div>
                </div>
              );
            })}
            {categoria.servizi?.length > 0 && (
              <div className="mb-2">
                <p className="text-xs font-bold text-slate-500 uppercase mb-2 px-1 border-b border-slate-200 pb-1">Altro</p>
                <div className="space-y-2">
                  {renderGroupedServices(categoria.servizi, activeCategory)}
                </div>
              </div>
            )}
          </>
        );
      } else if (activeSubcategory === "altro") {
        return (
          <div className="space-y-2">
            {renderGroupedServices(categoria.servizi || [], activeCategory)}
          </div>
        );
      } else {
        const sub = categoria.sottocategorie[activeSubcategory];
        if (!sub) return null;
        const { tiered, standalone } = groupServicesByTier(sub.servizi || []);
        return (
          <div className="space-y-2">
            {tiered.length > 0 && renderTierGroup(tiered, activeCategory, sub.nome)}
            {standalone.map(service => renderServiceCard(service, activeCategory))}
          </div>
        );
      }
    }

    // Senza sottocategorie - raggruppa anche qui i servizi simili
    return (
      <div className="space-y-2">
        {renderGroupedServices(categoria.servizi || [], activeCategory)}
      </div>
    );
  };

  // Raggruppa servizi "Altro" che hanno scaglioni simili (es. recupero fatture per fascia)
  const renderGroupedServices = (servizi, categoryKey) => {
    // Identifica gruppi per prefisso ID (es. recupero_fatture_emesse_90, _180, _360 → stesso gruppo)
    const groups = {};
    const standalone = [];
    
    servizi.forEach(s => {
      // Estrai prefisso: rimuovi l'ultimo segmento numerico/descrittivo
      const parts = s.id.split('_');
      const lastPart = parts[parts.length - 1];
      const isNumericSuffix = /^\d+k?$/.test(lastPart) || lastPart === 'oltre';
      
      if (isNumericSuffix && parts.length > 2) {
        const prefix = parts.slice(0, -1).join('_');
        if (!groups[prefix]) groups[prefix] = [];
        groups[prefix].push(s);
      } else {
        standalone.push(s);
      }
    });
    
    const result = [];
    
    // Render grouped tiers
    Object.entries(groups).forEach(([prefix, groupServizi]) => {
      if (groupServizi.length >= 2) {
        // Group name from the first service, removing the tier suffix
        const groupName = groupServizi[0].nome.replace(/\s*\(.*\)$/, '').replace(/\s*(fino a |da |oltre )?\d+.*$/, '').trim();
        result.push(
          <div key={prefix}>
            {renderTierGroup(groupServizi, categoryKey, groupName || prefix)}
          </div>
        );
      } else {
        groupServizi.forEach(s => standalone.push(s));
      }
    });
    
    // Render standalone services
    standalone.forEach(s => {
      result.push(renderServiceCard(s, categoryKey));
    });
    
    return result;
  };

  const totals = useMemo(() => {
    let subtotaleMonthly = 0;
    let subtotaleOneOff = 0;
    const serviziMonthly = [];
    const serviziOneOff = [];
    
    Object.entries(selectedServices).forEach(([key, service]) => {
      const { annualPrice } = calculateServicePrice(service, key);
      const billingType = service.billing_type || (
        service.tipo === "fisso_mensile" || 
        service.tipo === "fisso_trimestrale" || 
        service.tipo === "fisso_annuale" ? "monthly" : "one_off"
      );
      
      // Usa la descrizione personalizzata se presente
      const descrizioneFinale = serviceDescriptions[key] || service.descrizione;
      
      const servizioData = {
        categoria: service.categoria,
        nome: service.id === 'cont_ordinaria_600' && registrazioniOrd > 600 
          ? `Contabilità Ordinaria (${registrazioniOrd} registrazioni previste)`
          : service.nome,
        descrizione: descrizioneFinale,
        quantita: serviceQuantities[key] || 1,
        prezzo_unitario: annualPrice / (serviceQuantities[key] || 1),
        prezzo_totale: annualPrice,
        billing_type: billingType,
        parametri: { tipo: service.tipo }
      };
      
      if (billingType === "monthly") {
        subtotaleMonthly += annualPrice;
        serviziMonthly.push(servizioData);
      } else {
        subtotaleOneOff += annualPrice;
        serviziOneOff.push(servizioData);
      }
    });
    
    const subtotale = subtotaleMonthly + subtotaleOneOff;
    const scontoValore = subtotale * (scontoPercentuale / 100);
    const subtotaleScontato = subtotale - scontoValore;
    const cassaPrevidenza = subtotaleScontato * 0.04;
    const imponibile = subtotaleScontato + cassaPrevidenza;
    const iva = imponibile * 0.22;
    const totale = imponibile + iva;
    
    // Calcolo mensile solo sui servizi ricorrenti
    const scontoValoreMonthly = subtotaleMonthly * (scontoPercentuale / 100);
    const subtotaleScontatoMonthly = subtotaleMonthly - scontoValoreMonthly;
    const canoneNettoMensile = subtotaleScontatoMonthly / 12;
    const cassaPrevidenzaMonthly = subtotaleScontatoMonthly * 0.04;
    const imponibileMonthly = subtotaleScontatoMonthly + cassaPrevidenzaMonthly;
    const imponibileMensile = imponibileMonthly / 12;
    const totaleMensile = (imponibileMonthly * 1.22) / 12;
    
    return {
      subtotale,
      subtotaleMonthly,
      subtotaleOneOff,
      scontoValore,
      subtotaleScontato,
      cassaPrevidenza,
      imponibile,
      canoneNettoMensile,
      imponibileMensile,
      iva,
      totale,
      totaleMensile,
      servizi: [...serviziMonthly, ...serviziOneOff],
      serviziMonthly,
      serviziOneOff
    };
  }, [selectedServices, serviceQuantities, servicePrices, serviceDescriptions, scontoPercentuale, registrazioniOrd]);

  // Richiedi CF firmatario prima di scaricare documenti che lo richiedono
  const downloadWithCf = (downloadFn) => {
    const existingCf = cliente.cf_firmatario || savedPreventivo?.firmatario?.cf || savedPreventivo?.cliente?.cf_firmatario || "";
    if (existingCf) {
      setCfInput(existingCf);
      downloadFn(existingCf);
    } else {
      setCfInput("");
      setCfDialog({ open: true, callback: downloadFn });
    }
  };

  const confirmCfAndDownload = async () => {
    if (!cfInput || cfInput.length < 11) {
      toast.error("Inserisci un codice fiscale valido");
      return;
    }
    // Salva il CF nel preventivo
    try {
      const token = localStorage.getItem("auth_token");
      await axios.put(`${API}/preventivi/${savedPreventivo.id}`, {
        ...savedPreventivo,
        firmatario: { nome: cliente.legale_rappresentante || savedPreventivo?.cliente?.legale_rappresentante || "", cf: cfInput },
        cliente: { ...savedPreventivo.cliente, cf_firmatario: cfInput }
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { /* non bloccante */ }
    
    setCfDialog({ open: false, callback: null });
    cfDialog.callback?.(cfInput);
  };

  const handleSavePreventivo = async () => {
    if (!cliente.ragione_sociale) {
      setShowClienteDialog(true);
      return;
    }
    
    try {
      const preventivoData = {
        cliente,
        servizi: totals.servizi,
        subtotale: totals.subtotale,
        subtotale_monthly: totals.subtotaleMonthly,
        subtotale_one_off: totals.subtotaleOneOff,
        cassa_previdenza: totals.cassaPrevidenza,
        imponibile: totals.imponibile,
        iva: totals.iva,
        totale: totals.totale,
        totale_mensile: totals.totaleMensile,
        imponibile_mensile: totals.imponibileMensile,
        sconto_percentuale: scontoPercentuale,
        sconto_valore: totals.scontoValore,
        note: "",
        giorno_incasso: giornoIncasso,
        durata_contratto: "Biennale, poi annuale",
        modalita_pagamento: `Bonifico o RID/SEPA mensile entro il ${giornoIncasso} del mese`,
        recesso: "Dopo il biennio iniziale, disdetta con 6 mesi di preavviso",
        firmatario: {
          nome: cliente.legale_rappresentante || "",
          cf: cliente.cf_firmatario || ""
        }
      };
      
      const token = localStorage.getItem("auth_token");
      let response;
      
      if (isEditMode && editId) {
        // Aggiorna preventivo esistente
        response = await axios.put(`${API}/preventivi/${editId}`, preventivoData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Preventivo aggiornato!");
      } else {
        // Crea nuovo preventivo
        response = await axios.post(`${API}/preventivi`, preventivoData);
        toast.success("Preventivo salvato!");
      }
      
      setSavedPreventivo(response.data);
      return response.data;
    } catch (error) {
      console.error("Errore nel salvataggio:", error);
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
      return null;
    }
  };

  const handleShowPresentation = async () => {
    if (!cliente.ragione_sociale) {
      setShowClienteDialog(true);
      return;
    }
    
    const saved = await handleSavePreventivo();
    if (saved) {
      setSavedPreventivo(saved);
      setShowPresentazione(true);
    }
  };

  const renderServiceCard = (service, categoryKey) => {
    const key = `${categoryKey}_${service.id}`;
    const isSelected = !!selectedServices[key];
    const config = CATEGORY_CONFIG[categoryKey];
    
    return (
      <div 
        key={service.id}
        data-testid={`service-card-${service.id}`}
        onClick={() => addService(categoryKey, service.id)}
        className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
          isSelected 
            ? 'border-slate-300 bg-slate-100 opacity-50' 
            : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-slate-900 text-sm leading-tight">{service.nome}</h4>
            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{service.descrizione}</p>
          </div>
          
          <div className="text-right flex-shrink-0">
            {service.tipo === "fisso_mensile" && (
              <div className="font-mono text-sm font-bold" style={{ color: config.color }}>
                {formatCurrency(service.prezzo_mensile)}<span className="text-xs font-normal">/mese</span>
              </div>
            )}
            {service.tipo === "fisso_trimestrale" && (
              <div className="font-mono text-sm font-bold" style={{ color: config.color }}>
                {formatCurrency(service.prezzo_trimestrale)}<span className="text-xs font-normal">/trim</span>
              </div>
            )}
            {service.tipo === "fisso_annuale" && (
              <div className="font-mono text-sm font-bold" style={{ color: config.color }}>
                {formatCurrency(service.prezzo_annuale)}<span className="text-xs font-normal">/anno</span>
              </div>
            )}
            {service.tipo === "variabile" && (
              <div className="font-mono text-sm font-bold" style={{ color: config.color }}>
                {formatCurrency(service.prezzo_unitario || service.prezzo_orario)}<span className="text-xs font-normal">/{service.unita}</span>
              </div>
            )}
            {service.tipo === "una_tantum" && (
              <div className="font-mono text-sm font-bold" style={{ color: config.color }}>
                {formatCurrency(service.prezzo_unitario)}
              </div>
            )}
            {service.tipo === "variabile_descrizione" && (
              <div className="font-mono text-xs" style={{ color: config.color }}>
                {formatCurrency(service.prezzo_unitario)} <span className="text-slate-400">(modificabile)</span>
              </div>
            )}
            {service.tipo === "range" && (
              <div className="font-mono text-xs" style={{ color: config.color }}>
                {formatCurrency(service.prezzo_min)} - {formatCurrency(service.prezzo_max)}
              </div>
            )}
            {service.tipo === "base_piu_variabile" && (
              <div className="font-mono text-xs" style={{ color: config.color }}>
                {formatCurrency(service.prezzo_base)} + {formatCurrency(service.prezzo_per_dipendente)}/un.
              </div>
            )}
          </div>
        </div>
        
        {isSelected && (
          <div className="mt-2 text-xs text-emerald-600 font-medium">✓ Aggiunto</div>
        )}
      </div>
    );
  };

  const renderSelectedServiceCard = (key, service) => {
    const config = CATEGORY_CONFIG[service.categoria];
    const quantity = serviceQuantities[key] || 1;
    const customPrice = servicePrices[key];
    const { annualPrice } = calculateServicePrice(service, key);
    
    // Trova scaglioni fratelli per il selettore upgrade/downgrade
    const getSiblingTiers = () => {
      const cat = tariffe[service.categoria];
      if (!cat) return [];
      // Cerca nelle sottocategorie
      if (cat.sottocategorie) {
        for (const [, sub] of Object.entries(cat.sottocategorie)) {
          const found = sub.servizi?.find(s => s.id === service.id);
          if (found) {
            const { tiered } = groupServicesByTier(sub.servizi || []);
            return tiered.length >= 2 ? tiered : [];
          }
        }
      }
      // Cerca nei servizi diretti
      if (cat.servizi) {
        // Raggruppamento per prefisso
        const parts = service.id.split('_');
        const lastPart = parts[parts.length - 1];
        if ((/^\d+k?$/.test(lastPart) || lastPart === 'oltre') && parts.length > 2) {
          const prefix = parts.slice(0, -1).join('_');
          const siblings = cat.servizi.filter(s => {
            const sp = s.id.split('_');
            const sl = sp[sp.length - 1];
            return sp.slice(0, -1).join('_') === prefix && (/^\d+k?$/.test(sl) || sl === 'oltre');
          });
          return siblings.length >= 2 ? siblings : [];
        }
      }
      return [];
    };
    
    const siblings = getSiblingTiers();
    
    return (
      <div 
        key={key}
        className="p-3 rounded-lg border-l-4 bg-white shadow-sm"
        style={{ borderLeftColor: config.color }}
        data-testid={`selected-service-${service.id}`}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <h4 className="font-semibold text-slate-900 text-sm flex-1">{service.nome}</h4>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
            onClick={() => removeService(key)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Selettore scaglione se ci sono fratelli */}
        {siblings.length >= 2 && (
          <div className="mb-2" data-testid={`tier-selector-${service.id}`}>
            <Label className="text-xs text-slate-500 mb-1 block">Scaglione:</Label>
            <select
              value={service.id}
              onChange={(e) => switchTier(service.categoria, service.id, e.target.value)}
              className="w-full text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 font-medium"
              data-testid={`tier-select-${service.id}`}
            >
              {siblings.map(s => (
                <option key={s.id} value={s.id}>
                  {s.nome} — {s.prezzo_mensile ? `${formatCurrency(s.prezzo_mensile)}/mese` : s.prezzo_annuale ? `${formatCurrency(s.prezzo_annuale)}/anno` : s.prezzo_min ? `${formatCurrency(s.prezzo_min)}-${formatCurrency(s.prezzo_max)}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        
        {(service.tipo === "variabile" || service.tipo === "una_tantum" || service.tipo === "base_piu_variabile") && (
          <div className="flex items-center gap-2 mb-2">
            <Label className="text-xs text-slate-500">Qtà:</Label>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={() => updateQuantity(key, -1)}>
                <Minus className="w-3 h-3" />
              </Button>
              <Input 
                type="number" 
                value={quantity}
                onChange={(e) => setQuantity(key, e.target.value)}
                className="w-14 h-6 text-center font-mono text-xs"
              />
              <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={() => updateQuantity(key, 1)}>
                <Plus className="w-3 h-3" />
              </Button>
            </div>
            <span className="text-xs text-slate-400">{service.unita || 'un.'}</span>
          </div>
        )}
        
        {service.tipo === "range" && (
          <div className="space-y-1 mb-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-slate-500">Importo:</Label>
              <span className="font-mono text-sm font-bold" style={{ color: config.color }}>
                {formatCurrency(customPrice || service.prezzo_default)}
              </span>
            </div>
            <Slider
              value={[customPrice || service.prezzo_default]}
              min={service.prezzo_min}
              max={service.prezzo_max}
              step={1}
              onValueChange={([value]) => updatePrice(key, value)}
            />
          </div>
        )}
        
        {service.tipo === "variabile_descrizione" && (
          <div className="space-y-2 mb-2">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500 whitespace-nowrap">Importo €:</Label>
              <Input 
                type="number" 
                value={customPrice !== undefined ? customPrice : service.prezzo_unitario}
                onChange={(e) => updatePrice(key, e.target.value)}
                className="w-24 h-7 text-right font-mono text-sm"
                step="0.01"
                min="0"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Descrizione specifica:</Label>
              <Input 
                type="text" 
                value={serviceDescriptions[key] || ""}
                onChange={(e) => updateDescription(key, e.target.value)}
                className="w-full h-7 text-xs mt-1"
                placeholder={service.descrizione || "Inserisci dettagli..."}
              />
            </div>
          </div>
        )}
        
        <div className="flex justify-between items-center pt-2 border-t border-slate-100">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
            service.billing_type === 'monthly' 
              ? 'bg-blue-50 text-blue-700 border border-blue-200' 
              : 'bg-amber-50 text-amber-700 border border-amber-200'
          }`}>
            {service.billing_type === 'monthly' ? 'Mensile' : "All'occorrenza"}
          </span>
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-400">€</span>
            <input
              type="number"
              value={customPrice !== undefined ? customPrice : annualPrice}
              onChange={(e) => updatePrice(key, parseFloat(e.target.value) || 0)}
              className="w-20 text-right font-mono font-bold text-sm border border-transparent hover:border-slate-300 focus:border-blue-400 rounded px-1 py-0.5 outline-none transition-colors"
              style={{ color: config.color }}
              step="1"
              min="0"
              data-testid={`price-input-${service.id}`}
            />
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Mostra form cliente iniziale se non confermato
  if (!clienteConfermato) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-blue-500/30">
              <Handshake className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">SC Dottori Commercialisti</h1>
            <p className="text-white/60">Generatore Preventivi Professionale</p>
          </div>
          
          <Card className="shadow-2xl border-0 bg-white/10 backdrop-blur-xl">
            <CardHeader className="pb-4">
              <CardTitle className="text-white text-xl">Dati del Cliente</CardTitle>
              <p className="text-white/60 text-sm">Inserisci i dati per creare il preventivo personalizzato</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="init_ragione_sociale" className="text-white/80">Ragione Sociale *</Label>
                <Input
                  id="init_ragione_sociale"
                  value={cliente.ragione_sociale}
                  onChange={(e) => setCliente({...cliente, ragione_sociale: e.target.value})}
                  placeholder="Nome azienda o ditta"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="input-init-ragione-sociale"
                />
              </div>
              
              {/* Selezione CF o P.IVA */}
              <div className="space-y-2">
                <Label className="text-white/80">Identificativo Fiscale *</Label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2 text-white cursor-pointer">
                    <input
                      type="radio"
                      name="tipo_identificativo"
                      value="partita_iva"
                      checked={cliente.tipo_identificativo === "partita_iva"}
                      onChange={(e) => setCliente({...cliente, tipo_identificativo: e.target.value})}
                      className="w-4 h-4 text-violet-500"
                      data-testid="radio-partita-iva"
                    />
                    <span>Partita IVA</span>
                  </label>
                  <label className="flex items-center gap-2 text-white cursor-pointer">
                    <input
                      type="radio"
                      name="tipo_identificativo"
                      value="codice_fiscale"
                      checked={cliente.tipo_identificativo === "codice_fiscale"}
                      onChange={(e) => setCliente({...cliente, tipo_identificativo: e.target.value})}
                      className="w-4 h-4 text-violet-500"
                      data-testid="radio-codice-fiscale"
                    />
                    <span>Codice Fiscale</span>
                  </label>
                </div>
                {cliente.tipo_identificativo === "partita_iva" ? (
                  <Input
                    id="init_partita_iva"
                    value={cliente.partita_iva}
                    onChange={(e) => setCliente({...cliente, partita_iva: e.target.value})}
                    placeholder="12345678901"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    data-testid="input-init-partita-iva"
                  />
                ) : (
                  <Input
                    id="init_codice_fiscale"
                    value={cliente.codice_fiscale}
                    onChange={(e) => setCliente({...cliente, codice_fiscale: e.target.value.toUpperCase()})}
                    placeholder="RSSMRA80A01H501U"
                    maxLength={16}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 uppercase"
                    data-testid="input-init-codice-fiscale"
                  />
                )}
                <p className="text-white/50 text-xs mt-1">
                  {cliente.tipo_identificativo === "codice_fiscale" 
                    ? "Usa il CF se la P.IVA deve ancora essere aperta"
                    : "Inserisci la P.IVA della società"}
                </p>
              </div>
              
              {/* Indirizzo completo con autocomplete */}
              <div className="space-y-2">
                <Label className="text-white/80">Indirizzo Completo</Label>
                <div className="relative" ref={viaRef}>
                  <Input
                    id="init_via"
                    value={cliente.via}
                    onChange={(e) => handleViaChange(e.target.value)}
                    onFocus={() => cliente.via?.length >= 4 && handleViaChange(cliente.via)}
                    onBlur={() => setTimeout(() => setShowViaSuggestions(false), 200)}
                    placeholder="Via e numero civico"
                    autoComplete="off"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    data-testid="input-init-via"
                  />
                  {showViaSuggestions && viaSuggestions.length > 0 && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-56 overflow-y-auto">
                      {viaSuggestions.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          className="w-full px-3 py-2.5 text-left text-sm hover:bg-indigo-50 border-b border-slate-50 last:border-0"
                          onMouseDown={() => selectVia(s)}
                          data-testid={`via-suggestion-${i}`}
                        >
                          <div className="font-medium text-slate-800">{s.via}</div>
                          <div className="text-xs text-slate-400">{s.cap} {s.citta}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    id="init_cap"
                    value={cliente.cap}
                    onChange={(e) => setCliente({...cliente, cap: e.target.value})}
                    placeholder="CAP"
                    maxLength={5}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    data-testid="input-init-cap"
                  />
                  <div className="relative" ref={cittaRef}>
                    <Input
                      id="init_citta"
                      value={cliente.citta}
                      onChange={(e) => handleCittaChange(e.target.value)}
                      onFocus={() => cliente.citta?.length >= 2 && handleCittaChange(cliente.citta)}
                      onBlur={() => setTimeout(() => setShowCittaSuggestions(false), 200)}
                      placeholder="Città"
                      autoComplete="off"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      data-testid="input-init-citta"
                    />
                    {showCittaSuggestions && cittaSuggestions.length > 0 && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                        {cittaSuggestions.map((c, i) => (
                          <button
                            key={i}
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 flex justify-between items-center border-b border-slate-50 last:border-0"
                            onMouseDown={() => selectComune(c)}
                            data-testid={`suggestion-${i}`}
                          >
                            <span className="font-medium text-slate-800">{c.n}</span>
                            <span className="text-slate-400 text-xs">{c.s} — {c.c[0]}{c.c.length > 1 ? ` (+${c.c.length - 1})` : ''}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Input
                    id="init_provincia"
                    value={cliente.provincia}
                    onChange={(e) => setCliente({...cliente, provincia: e.target.value.toUpperCase()})}
                    placeholder="Prov."
                    maxLength={2}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 uppercase"
                    data-testid="input-init-provincia"
                  />
                </div>
              </div>
              
              {/* Legale Rappresentante */}
              <div className="space-y-2">
                <Label htmlFor="init_legale_rapp" className="text-white/80">Legale Rappresentante (per società)</Label>
                <Input
                  id="init_legale_rapp"
                  value={cliente.legale_rappresentante}
                  onChange={(e) => setCliente({...cliente, legale_rappresentante: e.target.value})}
                  placeholder="Nome e Cognome del legale rappresentante"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="input-init-legale-rapp"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="init_email" className="text-white/80">Email</Label>
                  <Input
                    id="init_email"
                    type="email"
                    value={cliente.email}
                    onChange={(e) => setCliente({...cliente, email: e.target.value})}
                    placeholder="info@azienda.it"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="init_telefono" className="text-white/80">Telefono</Label>
                  <Input
                    id="init_telefono"
                    value={cliente.telefono}
                    onChange={(e) => setCliente({...cliente, telefono: e.target.value})}
                    placeholder="0544 123456"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                </div>
              </div>
              
              {/* Checkbox Cliente Esistente */}
              <div className="flex items-center space-x-3 mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
                <input
                  type="checkbox"
                  id="cliente_esistente"
                  checked={cliente.cliente_esistente}
                  onChange={(e) => setCliente({...cliente, cliente_esistente: e.target.checked})}
                  className="w-5 h-5 rounded border-white/30 bg-white/10 text-violet-500 focus:ring-violet-500 cursor-pointer"
                  data-testid="checkbox-cliente-esistente"
                />
                <label htmlFor="cliente_esistente" className="text-white cursor-pointer">
                  <span className="font-medium">Cliente già acquisito</span>
                  <p className="text-white/60 text-sm">Seleziona per una presentazione focalizzata sul preventivo specifico</p>
                </label>
              </div>
              
              <Button 
                className="w-full mt-6 bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700 text-white font-semibold py-6"
                onClick={() => {
                  if (!cliente.ragione_sociale) {
                    toast.error("Inserisci la ragione sociale");
                    return;
                  }
                  // Verifica identificativo fiscale in base al tipo selezionato
                  if (cliente.tipo_identificativo === "partita_iva" && !cliente.partita_iva) {
                    toast.error("Inserisci la Partita IVA");
                    return;
                  }
                  if (cliente.tipo_identificativo === "codice_fiscale" && !cliente.codice_fiscale) {
                    toast.error("Inserisci il Codice Fiscale");
                    return;
                  }
                  setClienteConfermato(true);
                  toast.success("Dati cliente salvati!");
                }}
                data-testid="btn-conferma-cliente"
              >
                <ChevronRight className="w-5 h-5 mr-2" />
                Inizia a creare il preventivo
              </Button>
            </CardContent>
          </Card>
          
          <div className="text-center mt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.location.href = '/dashboard'}
              data-testid="btn-dashboard-init"
              className="text-white/60 hover:text-white hover:bg-white/10"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Vai alla Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-pink-50 to-sky-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center">
                <Handshake className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">
                  SC Dottori Commercialisti
                  {isEditMode && (
                    <span className="ml-2 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                      Modifica
                    </span>
                  )}
                </h1>
                <p className="text-xs text-slate-500">
                  {isEditMode ? `Modifica preventivo — ${cliente.ragione_sociale}` : 'Generatore Preventivi'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowClienteDialog(true)}
                data-testid="btn-cliente"
                className="font-medium"
              >
                <Building2 className="w-4 h-4 mr-2" />
                {cliente.ragione_sociale || "Dati Cliente"}
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.href = '/dashboard'}
                data-testid="btn-dashboard"
                className="font-medium border-blue-200 text-blue-700 hover:bg-blue-50"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { localStorage.removeItem("auth_token"); localStorage.removeItem("auth_nome"); window.location.href = "/"; }}
                data-testid="btn-logout"
                className="text-slate-500 hover:text-red-600 hover:bg-red-50"
                title="Esci"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 py-6">
        <div className="flex gap-6">
          {/* Left - Catalogo servizi con accordion */}
          <div className="w-80 flex-shrink-0">
            <div className="sticky top-20 space-y-2">
              {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                const isOpen = activeCategory === key;
                const selectedCount = Object.keys(selectedServices).filter(k => k.startsWith(key)).length;
                
                return (
                  <div key={key} className="rounded-xl overflow-hidden shadow-sm border border-slate-200 bg-white">
                    {/* Intestazione categoria */}
                    <button
                      data-testid={`tab-${key}`}
                      onClick={() => {
                        setActiveCategory(isOpen ? null : key);
                        setActiveSubcategory(null);
                      }}
                      className={`w-full px-4 py-3 font-semibold flex items-center gap-3 transition-all ${
                        ['#FDE68A', '#6EE7B7', '#7DD3FC'].includes(config.color) ? 'text-slate-800' : 'text-white'
                      } ${isOpen ? 'brightness-105' : 'brightness-95 hover:brightness-100'}`}
                      style={{ backgroundColor: config.color }}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm flex-1 text-left">{config.label}</span>
                      {selectedCount > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-white/30">
                          {selectedCount}
                        </span>
                      )}
                      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Contenuto espanso */}
                    {isOpen && (
                      <div className="border-t border-slate-100 max-h-[60vh] overflow-y-auto">
                        {/* Sottocategorie */}
                        {getSubcategories().length > 0 && (
                          <div className="flex flex-wrap gap-1 p-3 pb-1 border-b border-slate-100 bg-slate-50">
                            <Button
                              size="sm"
                              variant={activeSubcategory === null ? "default" : "outline"}
                              className="text-xs px-3 py-1 h-6"
                              onClick={() => setActiveSubcategory(null)}
                            >
                              Tutti
                            </Button>
                            {getSubcategories().map(([subKey, sub]) => (
                              <Button
                                key={subKey}
                                size="sm"
                                variant={activeSubcategory === subKey ? "default" : "outline"}
                                className="text-xs px-3 py-1 h-6"
                                onClick={() => setActiveSubcategory(subKey)}
                              >
                                {sub.nome}
                              </Button>
                            ))}
                          </div>
                        )}
                        <div className="p-3 space-y-2">
                          {renderServicesForCategory()}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Center/Right - Il Preventivo */}
          <div className="flex-1 min-w-0">
            <Card className="shadow-lg border-0">
              <CardHeader className="pb-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-t-xl">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="w-5 h-5 text-amber-400" />
                  Il Tuo Preventivo
                  {Object.keys(selectedServices).length > 0 && (
                    <span className="text-sm font-normal text-slate-300">
                      ({Object.keys(selectedServices).length} servizi)
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {Object.keys(selectedServices).length > 0 ? (
                  <div className="space-y-6">
                    {/* Lista servizi raggruppati per categoria */}
                    {(() => {
                      // Raggruppa servizi selezionati per categoria
                      const grouped = {};
                      Object.entries(selectedServices).forEach(([key, service]) => {
                        const cat = key.split('_')[0];
                        if (!grouped[cat]) grouped[cat] = [];
                        grouped[cat].push({ key, service });
                      });
                      
                      return Object.entries(grouped).map(([cat, items]) => {
                        const config = CATEGORY_CONFIG[cat];
                        if (!config) return null;
                        return (
                          <div key={cat}>
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                              <h3 className="text-sm font-bold text-slate-700">{config.label}</h3>
                              <span className="text-xs text-slate-400">({items.length})</span>
                            </div>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                              {items.map(({ key, service }) => 
                                renderSelectedServiceCard(key, service)
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                    
                    {/* Impostazioni e Totali - layout a griglia */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      {/* Sconto */}
                      <div className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg border border-violet-200">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-sm font-semibold text-violet-900">Sconto personalizzato</Label>
                          <span className="font-mono text-xl font-bold text-violet-600">{scontoPercentuale}%</span>
                        </div>
                        <Slider
                          value={[scontoPercentuale]}
                          min={0}
                          max={50}
                          step={1}
                          onValueChange={([value]) => setScontoPercentuale(value)}
                          data-testid="slider-sconto"
                        />
                      </div>
                      
                      {/* Giorno incasso */}
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between">
                        <Label className="text-sm font-semibold text-slate-700">Giorno incasso mensile</Label>
                        <input
                          type="number"
                          min={1}
                          max={28}
                          value={giornoIncasso}
                          onChange={(e) => setGiornoIncasso(Math.min(28, Math.max(1, parseInt(e.target.value) || 10)))}
                          className="w-16 text-center font-mono text-lg font-bold border border-slate-300 rounded-md px-2 py-1"
                          data-testid="input-giorno-incasso"
                        />
                      </div>
                      
                      {/* Registrazioni contabilità ordinaria */}
                      {Object.values(selectedServices).some(s => s.id === 'cont_ordinaria_600') && (
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <Label className="text-sm font-semibold text-blue-900 block mb-2">Registrazioni annue previste</Label>
                          <div className="flex items-center gap-3">
                            <input
                              type="number"
                              min={100}
                              max={10000}
                              step={50}
                              value={registrazioniOrd}
                              onChange={(e) => setRegistrazioniOrd(Math.max(100, parseInt(e.target.value) || 600))}
                              className="w-24 text-center font-mono text-lg font-bold border border-blue-300 rounded-md px-2 py-1"
                              data-testid="input-registrazioni-ord"
                            />
                            <div className="text-xs text-blue-700 leading-tight">
                              {registrazioniOrd <= 600 ? (
                                <span>Base inclusa (fino a 600)</span>
                              ) : registrazioniOrd <= 2000 ? (
                                <span>{registrazioniOrd - 600} extra × €2,75 = <strong>€{((registrazioniOrd - 600) * 2.75).toFixed(0)}</strong></span>
                              ) : (
                                <span>{registrazioniOrd - 600} extra = <strong>€{((2000-600)*2.75 + (registrazioniOrd-2000)*2.40).toFixed(0)}</strong></span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Totali e Azioni */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Totali */}
                      <div className="p-6 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-xl text-white">
                        {/* Canone Mensile */}
                        {totals.serviziMonthly.length > 0 && (
                          <div className="text-center mb-4">
                            <p className="text-slate-300 text-sm mb-1">Canone Mensile</p>
                            <p className="font-mono text-5xl font-extrabold text-white">
                              {formatCurrency(totals.canoneNettoMensile)}
                            </p>
                            <p className="text-slate-300 text-xs mt-1">+ Cassa prev. 4% + IVA 22%</p>
                            <p className="text-white/70 text-sm mt-2">
                              Netto annuo canone: <span className="font-mono font-semibold text-white">{formatCurrency(totals.subtotaleMonthly)}</span>
                            </p>
                          </div>
                        )}
                        
                        {/* Servizi una tantum */}
                        {totals.serviziOneOff.length > 0 && (
                          <>
                            <Separator className="bg-white/20 my-3" />
                            <div className="mb-3">
                              <p className="text-amber-300 text-sm font-semibold mb-2">
                                Servizi da fatturare all'erogazione:
                              </p>
                              <div className="space-y-1 max-h-32 overflow-y-auto">
                                {totals.serviziOneOff.map((s, i) => (
                                  <div key={i} className="flex justify-between text-xs">
                                    <span className="text-white/80 truncate pr-2">{s.nome}</span>
                                    <span className="font-mono text-white/90 flex-shrink-0">{formatCurrency(s.prezzo_totale)}</span>
                                  </div>
                                ))}
                              </div>
                              <div className="flex justify-between mt-2 pt-2 border-t border-white/20">
                                <span className="text-amber-300 font-semibold text-sm">Totale una tantum:</span>
                                <span className="font-mono font-bold text-amber-300">{formatCurrency(totals.subtotaleOneOff)}</span>
                              </div>
                              <p className="text-white/50 text-xs mt-1">+ Cassa prev. 4% + IVA 22%</p>
                            </div>
                          </>
                        )}
                      </div>
                      
                      {/* Azioni e condizioni */}
                      <div className="space-y-4">
                        {/* Info contratto */}
                        {totals.serviziMonthly.length > 0 && (
                          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                            <p className="font-semibold mb-1">Condizioni contrattuali:</p>
                            <p>Durata iniziale: <strong>biennale</strong></p>
                            <p>Dopo il biennio: rinnovo <strong>annuale</strong></p>
                            <p>Disdetta: <strong>6 mesi</strong> prima della scadenza</p>
                            <p>Pagamento: mensile entro il {giornoIncasso} con bonifico o RID/SEPA</p>
                          </div>
                        )}
                        
                        {/* Buttons */}
                        <div className="grid grid-cols-2 gap-3">
                          <Button 
                            className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 shadow-lg py-6"
                            onClick={handleShowPresentation}
                            data-testid="btn-presentazione"
                          >
                            <Sparkles className="w-5 h-5 mr-2" />
                            Presentazione
                          </Button>
                          <Button 
                            variant="outline"
                            className="py-6"
                            onClick={handleSavePreventivo}
                            data-testid="btn-salva"
                          >
                            <Download className="w-5 h-5 mr-2" />
                            {isEditMode ? 'Aggiorna' : 'Salva'}
                          </Button>
                        </div>
                    
                    {/* Download Documents - PDF (stessi inviati via email) */}
                    {savedPreventivo && (
                      <div className="space-y-3 mt-3">
                        {/* Link condivisibile */}
                        {savedPreventivo.share_token && (
                          <Button
                            variant="outline"
                            className="w-full py-4 border-blue-300 text-blue-700 hover:bg-blue-50"
                            onClick={() => {
                              const link = `${window.location.origin}/p/${savedPreventivo.share_token}`;
                              navigator.clipboard.writeText(link);
                              toast.success("Link copiato! Invialo al cliente");
                            }}
                            data-testid="btn-copia-link"
                          >
                            <Link2 className="w-4 h-4 mr-2" />
                            Copia link presentazione
                          </Button>
                        )}
                        <p className="text-xs text-slate-500 text-center">Documenti PDF (identici a quelli inviati al cliente)</p>
                        <div className="grid grid-cols-2 gap-3">
                          <Button 
                            variant="outline"
                            className="py-4 text-xs"
                            onClick={() => downloadDocument(
                              `${API}/preventivi/${savedPreventivo.id}/preventivo-pdf`,
                              `Preventivo_${savedPreventivo.numero_preventivo}.pdf`
                            )}
                            data-testid="btn-download-preventivo"
                          >
                            <FileText className="w-4 h-4 mr-1" />
                            Preventivo PDF
                          </Button>
                          <Button 
                            variant="outline"
                            className="py-4 text-xs"
                            onClick={() => downloadWithCf((cf) => downloadDocument(
                              `${API}/preventivi/${savedPreventivo.id}/lettera-incarico-pdf?cf_firmatario=${encodeURIComponent(cf)}`,
                              `Lettera_Incarico_${savedPreventivo.numero_preventivo}.pdf`
                            ))}
                            data-testid="btn-download-lettera"
                          >
                            <FileCheck className="w-4 h-4 mr-1" />
                            Lettera Incarico PDF
                          </Button>
                        </div>
                        
                        {/* Allegati B e C */}
                        <div className="grid grid-cols-2 gap-3">
                          <Button 
                            variant="outline"
                            className="py-4 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                            onClick={() => downloadDocument(
                              `${API}/preventivi/${savedPreventivo.id}/allegato-b-pdf`,
                              `AllegatoB_Condizioni_${savedPreventivo.numero_preventivo}.pdf`
                            )}
                            data-testid="btn-download-allegato-b"
                          >
                            <FileSignature className="w-4 h-4 mr-1" />
                            Allegato B PDF
                          </Button>
                          <Button 
                            variant="outline"
                            className="py-4 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => downloadDocument(
                              `${API}/preventivi/${savedPreventivo.id}/allegato-c-pdf`,
                              `AllegatoC_Privacy_${savedPreventivo.numero_preventivo}.pdf`
                            )}
                            data-testid="btn-download-allegato-c"
                          >
                            <FileDown className="w-4 h-4 mr-1" />
                            Allegato C PDF
                          </Button>
                        </div>
                        
                        {/* Condividi presentazione */}
                        <div className="pt-3 border-t border-slate-200">
                          <p className="text-xs text-slate-500 mb-2 text-center">Invia la presentazione al cliente</p>
                          <div className="grid grid-cols-2 gap-3">
                            <Button 
                              variant="outline"
                              className="py-4 text-xs border-green-400 text-green-700 hover:bg-green-50"
                              onClick={() => {
                                const url = savedPreventivo.share_token ? `${window.location.origin}/p/${savedPreventivo.share_token}` : `${window.location.origin}/presentazione/${savedPreventivo.id}`;
                                const text = `Proposta di collaborazione - ${savedPreventivo.cliente.ragione_sociale}`;
                                window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`, '_blank');
                              }}
                              data-testid="btn-share-whatsapp"
                            >
                              <MessageCircle className="w-4 h-4 mr-1" />
                              WhatsApp
                            </Button>
                            <Button 
                              variant="outline"
                              className="py-4 text-xs border-blue-400 text-blue-700 hover:bg-blue-50"
                              onClick={() => {
                                const url = savedPreventivo.share_token ? `${window.location.origin}/p/${savedPreventivo.share_token}` : `${window.location.origin}/presentazione/${savedPreventivo.id}`;
                                const subject = `Proposta di collaborazione - ${savedPreventivo.cliente.ragione_sociale}`;
                                const body = `Gentile ${savedPreventivo.cliente.legale_rappresentante || 'Cliente'},\n\nLe invio la nostra proposta di collaborazione.\n\nVisualizza la presentazione:\n${url}\n\nCordiali saluti,\nSC Dottori Commercialisti`;
                                window.open(`mailto:${savedPreventivo.cliente.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
                              }}
                              data-testid="btn-share-email"
                            >
                              <Mail className="w-4 h-4 mr-1" />
                              Email
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <Calculator className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium mb-2">Preventivo vuoto</p>
                    <p className="text-sm">Seleziona i servizi dalle categorie</p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Sezione NON INCLUSO */}
            {Object.keys(selectedServices).length > 0 && (
              <Card className="mt-4 shadow-lg border-0">
                <CardHeader className="pb-2 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-t-xl">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    Servizi NON inclusi nel presente preventivo
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {SERVIZI_NON_INCLUSI.map((s, i) => (
                      <div key={i} className="flex justify-between text-xs p-2 bg-slate-50 rounded">
                        <span className="text-slate-700">{s.nome}</span>
                        <span className="font-mono text-slate-900 font-semibold">{s.prezzo}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                    <p className="text-xs text-blue-900 flex items-start gap-2">
                      <TrendingUp className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span><strong>Adeguamenti per superamento volumi:</strong> In caso di superamento delle soglie previste, il compenso sarà adeguato allo scaglione superiore dal periodo successivo.</span>
                    </p>
                  </div>
                  
                  <div className="grid gap-1">
                    {ADEGUAMENTI_VOLUMI.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs p-2 bg-slate-50 rounded">
                        <span className="font-semibold text-slate-700 w-40">{a.servizio}</span>
                        <span className="text-slate-500 flex-1">{a.descrizione}</span>
                        <span className="font-mono text-slate-900">{a.adeguamento}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 p-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg text-xs">
                    <p>Per tutto quanto non espressamente indicato nel presente preventivo, si fa riferimento alle <strong>tariffe minime degli Onorari Consigliati</strong> pubblicati dall'Associazione Nazionale Commercialisti per l'anno in corso.</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Cliente Dialog */}
      <Dialog open={showClienteDialog} onOpenChange={setShowClienteDialog}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dati Cliente</DialogTitle>
            <DialogDescription>Inserisci i dati del cliente per la lettera d'incarico</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="ragione_sociale">Ragione Sociale *</Label>
              <Input
                id="ragione_sociale"
                value={cliente.ragione_sociale}
                onChange={(e) => setCliente({...cliente, ragione_sociale: e.target.value})}
                placeholder="Nome azienda"
                data-testid="input-ragione-sociale"
              />
            </div>
            
            {/* Selezione CF o P.IVA */}
            <div className="grid gap-2">
              <Label>Identificativo Fiscale *</Label>
              <div className="flex gap-4 mb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="dialog_tipo_identificativo"
                    value="partita_iva"
                    checked={cliente.tipo_identificativo === "partita_iva"}
                    onChange={(e) => setCliente({...cliente, tipo_identificativo: e.target.value})}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Partita IVA</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="dialog_tipo_identificativo"
                    value="codice_fiscale"
                    checked={cliente.tipo_identificativo === "codice_fiscale"}
                    onChange={(e) => setCliente({...cliente, tipo_identificativo: e.target.value})}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Codice Fiscale</span>
                </label>
              </div>
              {cliente.tipo_identificativo === "partita_iva" ? (
                <Input
                  id="partita_iva"
                  value={cliente.partita_iva}
                  onChange={(e) => setCliente({...cliente, partita_iva: e.target.value})}
                  placeholder="12345678901"
                />
              ) : (
                <Input
                  id="codice_fiscale"
                  value={cliente.codice_fiscale}
                  onChange={(e) => setCliente({...cliente, codice_fiscale: e.target.value.toUpperCase()})}
                  placeholder="RSSMRA80A01H501U"
                  maxLength={16}
                  className="uppercase"
                />
              )}
              <p className="text-xs text-muted-foreground">
                {cliente.tipo_identificativo === "codice_fiscale" 
                  ? "Usa il CF se la P.IVA deve ancora essere aperta"
                  : "Inserisci la P.IVA della società"}
              </p>
            </div>
            
            {/* Indirizzo completo */}
            <div className="grid gap-2">
              <Label>Indirizzo Completo</Label>
              <div className="relative">
                <Input
                  id="via"
                  value={cliente.via}
                  onChange={(e) => handleViaChange(e.target.value)}
                  onFocus={() => cliente.via?.length >= 4 && handleViaChange(cliente.via)}
                  onBlur={() => setTimeout(() => setShowViaSuggestions(false), 200)}
                  placeholder="Via e numero civico"
                  autoComplete="off"
                />
                {showViaSuggestions && viaSuggestions.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                    {viaSuggestions.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 border-b border-slate-50 last:border-0"
                        onMouseDown={() => selectVia(s)}
                      >
                        <div className="font-medium text-slate-800">{s.via}</div>
                        <div className="text-xs text-slate-400">{s.cap} {s.citta}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input
                  id="cap"
                  value={cliente.cap}
                  onChange={(e) => setCliente({...cliente, cap: e.target.value})}
                  placeholder="CAP"
                  maxLength={5}
                />
                <div className="relative">
                  <Input
                    id="citta"
                    value={cliente.citta}
                    onChange={(e) => handleCittaChange(e.target.value)}
                    onFocus={() => cliente.citta?.length >= 2 && handleCittaChange(cliente.citta)}
                    onBlur={() => setTimeout(() => setShowCittaSuggestions(false), 200)}
                    placeholder="Città"
                    autoComplete="off"
                  />
                  {showCittaSuggestions && cittaSuggestions.length > 0 && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                      {cittaSuggestions.map((c, i) => (
                        <button
                          key={i}
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 flex justify-between items-center border-b border-slate-50 last:border-0"
                          onMouseDown={() => selectComune(c)}
                        >
                          <span className="font-medium text-slate-800">{c.n}</span>
                          <span className="text-slate-400 text-xs">{c.s} — {c.c[0]}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Input
                  id="provincia"
                  value={cliente.provincia}
                  onChange={(e) => setCliente({...cliente, provincia: e.target.value.toUpperCase()})}
                  placeholder="Prov."
                  maxLength={2}
                  className="uppercase"
                />
              </div>
            </div>
            
            {/* Legale Rappresentante */}
            <div className="grid gap-2">
              <Label htmlFor="legale_rappresentante">Legale Rappresentante (per società)</Label>
              <Input
                id="legale_rappresentante"
                value={cliente.legale_rappresentante}
                onChange={(e) => setCliente({...cliente, legale_rappresentante: e.target.value})}
                placeholder="Nome e Cognome"
                data-testid="input-legale-rappresentante"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={cliente.email}
                  onChange={(e) => setCliente({...cliente, email: e.target.value})}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="pec">PEC</Label>
                <Input
                  id="pec"
                  value={cliente.pec}
                  onChange={(e) => setCliente({...cliente, pec: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="telefono">Telefono</Label>
              <Input
                id="telefono"
                value={cliente.telefono}
                onChange={(e) => setCliente({...cliente, telefono: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClienteDialog(false)}>Annulla</Button>
            <Button 
              onClick={() => {
                if (!cliente.ragione_sociale) {
                  toast.error("Inserisci la ragione sociale");
                  return;
                }
                setShowClienteDialog(false);
                toast.success("Dati salvati");
              }}
              data-testid="btn-salva-cliente"
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Presentation Mode - Professional */}
      {showPresentazione && savedPreventivo && (
        <PresentationProfessional 
          preventivo={savedPreventivo}
          onClose={() => setShowPresentazione(false)}
          clienteEsistente={cliente.cliente_esistente}
        />
      )}

      {/* Dialog CF Firmatario per download documenti */}
      <Dialog open={cfDialog.open} onOpenChange={(open) => !open && setCfDialog({ open: false, callback: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Codice Fiscale del firmatario</DialogTitle>
            <DialogDescription>
              Inserisci il codice fiscale del legale rappresentante che firmerà i documenti.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Codice Fiscale</Label>
              <Input
                value={cfInput}
                onChange={(e) => setCfInput(e.target.value.toUpperCase())}
                placeholder="RSSMRA85M01H501Z"
                maxLength={16}
                className="uppercase font-mono text-lg tracking-wider"
                data-testid="input-cf-dialog"
                onKeyDown={(e) => e.key === 'Enter' && confirmCfAndDownload()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCfDialog({ open: false, callback: null })}>Annulla</Button>
            <Button onClick={confirmCfAndDownload} data-testid="btn-confirm-cf">
              Conferma e Scarica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
